// Pure builders for the booking payloads the cart checkout POSTs to
// /api/bookings and /api/group-bookings.
//
// Extracted from CartCheckoutForm so the booking contract can be unit-tested
// without rendering the whole component. The critical invariant these encode:
// every booking payload carries `activity_id` (+ a derived `variant`) so the
// compta product resolver can map it. Omitting them was the bug that dropped
// cart bookings into needs_review (and, before migration 149, made them vanish).
import { deriveVariant } from '@/features/booking/deriveVariant'
import type { CartItem } from '@/types/cart.types'

export interface CartCustomer {
  firstName: string
  lastName: string
  phone: string
  fullName: string
  accommodation?: string | null
}

// Flight activities map their slot to a fixed activity_time.
export function activityTimeFromFlightTime(
  flightTime?: 'sunrise' | 'sunset'
): string | null {
  if (flightTime === 'sunrise') return '05:30'
  if (flightTime === 'sunset') return '16:00'
  return null
}

function participantDetailsFor(item: CartItem, fullName: string) {
  return (
    item.guestDetails?.map((g) => ({
      name: g.name,
      weight: g.weight || 0,
    })) || [{ name: fullName, weight: 0 }]
  )
}

export function buildSingleBookingPayload(
  item: CartItem,
  customer: CartCustomer
) {
  return {
    // activity_id + variant drive the compta resolver; product_id kept for
    // backwards compatibility but ignored server-side.
    activity_id: item.activityId,
    variant:
      deriveVariant({ timeSlot: item.flightTime, tourType: item.tourType }) ??
      undefined,
    product_id: item.activityId,
    guest_first_name: customer.firstName,
    guest_last_name: customer.lastName,
    guest_phone: customer.phone,
    activity_date: item.selectedDate,
    activity_time: activityTimeFromFlightTime(item.flightTime),
    number_of_pax: item.partySize,
    participant_details: participantDetailsFor(item, customer.fullName),
    total_amount: item.subtotal,
    currency: 'USD',
    status: 'pending',
    notes: item.notes || null,
    source: 'website-cart',
    activity_name: item.activityTitle,
    hotel: customer.accommodation || null,
  }
}

export function buildGroupBookingPayload(
  items: CartItem[],
  customer: CartCustomer
) {
  return {
    guest_first_name: customer.firstName,
    guest_last_name: customer.lastName,
    guest_phone: customer.phone,
    currency: 'USD',
    notes: `Group booking from website cart`,
    source: 'website-cart',
    hotel: customer.accommodation || null,
    activities: items.map((item) => ({
      activity_id: item.activityId,
      variant:
        deriveVariant({ timeSlot: item.flightTime, tourType: item.tourType }) ??
        undefined,
      activity_name: item.activityTitle,
      activity_date: item.selectedDate,
      activity_time: activityTimeFromFlightTime(item.flightTime),
      number_of_pax: item.partySize,
      participant_details: participantDetailsFor(item, customer.fullName),
      total_amount: item.subtotal,
      notes: item.notes || null,
    })),
  }
}
